import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle2, MessageSquare, Star } from 'lucide-react';

export default function FeedbackSubmitPage() {
  const { code } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/feedback/lookup/${code}`)
      .then(r => setForm(r.data?.found ? r.data : null))
      .catch(() => setForm(null))
      .finally(() => setLoading(false));
  }, [code]);

  const setA = (qid, v) => setAnswers(a => ({ ...a, [qid]: v }));

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      await api.post(`/feedback/submit/${code}`, {
        respondent_name: form.anonymous ? null : (name || null),
        answers,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  if (!form) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="feedback-notfound">
      <div className="bg-white rounded-2xl shadow border p-8 text-center max-w-md">
        <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-3" />
        <div className="font-display font-bold text-xl">Form unavailable</div>
        <p className="text-sm text-muted-foreground mt-1">This feedback link is invalid or closed.</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="feedback-thankyou">
      <div className="bg-white rounded-2xl shadow border p-8 text-center max-w-md">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-3" />
        <div className="font-display font-bold text-2xl">Thank you!</div>
        <p className="text-sm text-muted-foreground mt-1">Your feedback has been recorded.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="feedback-submit-page">
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl border p-7 space-y-6">
        <div>
          <div className="text-amber-500 font-display font-bold text-sm mb-2">Lumyn Feedback</div>
          <h1 className="font-display text-2xl font-bold text-[#1A3C6E]">{form.title}</h1>
          {form.description && <p className="text-sm text-muted-foreground mt-1">{form.description}</p>}
        </div>

        {!form.anonymous && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Your name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" data-testid="feedback-name-input" />
          </div>
        )}

        {(form.questions || []).map((q, i) => (
          <div key={q.id} className="space-y-2" data-testid={`feedback-question-${i}`}>
            <label className="text-sm font-medium">{q.prompt}{q.required && <span className="text-rose-500 ml-1">*</span>}</label>
            {q.type === 'rating' && (
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" onClick={() => setA(q.id, n)} data-testid={`feedback-rating-${i}-${n}`}>
                    <Star className={`w-7 h-7 ${answers[q.id] >= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                  </button>
                ))}
              </div>
            )}
            {q.type === 'nps' && (
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: 11 }, (_, n) => (
                  <button key={n} type="button" onClick={() => setA(q.id, n)}
                    className={`w-9 h-9 rounded-md border text-sm font-medium ${answers[q.id] === n ? 'bg-[#1A3C6E] text-white border-[#1A3C6E]' : 'bg-white hover:bg-slate-50'}`}
                    data-testid={`feedback-nps-${i}-${n}`}>{n}</button>
                ))}
              </div>
            )}
            {q.type === 'choice' && (
              <div className="space-y-1.5">
                {q.options.map((opt, oi) => (
                  <label key={oi} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name={q.id} checked={answers[q.id] === opt} onChange={() => setA(q.id, opt)} data-testid={`feedback-choice-${i}-${oi}`} />
                    {opt}
                  </label>
                ))}
              </div>
            )}
            {q.type === 'text' && (
              <Textarea value={answers[q.id] || ''} onChange={(e) => setA(q.id, e.target.value)} rows={3} data-testid={`feedback-text-${i}`} />
            )}
          </div>
        ))}

        {error && <p className="text-sm text-rose-600" data-testid="feedback-error">{error}</p>}
        <Button onClick={submit} disabled={submitting} className="w-full" data-testid="feedback-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit feedback'}
        </Button>
      </div>
    </div>
  );
}
